import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { GlbFormContext, LoaderContext, CompanyContext } from '../App';
import { ASSESSMENTFORMTXT, GENERAL_FORM, QuestionType } from '../core/constants/common';
import CarbonEmissionService from '../core/services/CarbonEmissionService';
import FormService from '../core/services/FormService';
import FormEditService from '../core/services/FromEditService';
import Utils from '../core/utilities/Utils';
import { ACTIONS_ARR_OBJ, OPTION_OBJ, SAVE_STATUS } from '../features/builder/builderEditorConstants';
import {
  buildFieldPayload,
  buildOptionIdMap,
  cloneActionsForDuplicate,
  cloneFieldPayloadForDuplicate,
  cloneLogicsForDuplicate,
  isActionNeeded,
  isRemarkNeeded,
  mapGridConfigToEditorData,
  mapGridQuestionConfigToEditorData,
  mapTableConfigToEditorData,
} from '../features/builder/payloadBuilders';
import {
  downloadFormExport,
  fetchFullFormExport,
  importFormIntoForm,
  readImportFile,
  validateFormTree,
  validateImportFile,
} from '../features/builder/formPortability';

const genGridQuestionKey = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Columns/rows are a flat ordered list — "Add column"/"Add row" always just
// appends to it, one button each, nothing else to set up first. Spanning
// headers (columnGroups/rowGroups) are a separate, optional overlay that
// references a contiguous run of existing column/row keys, so grouping never
// has to be decided before a simple grid can be built.
const defaultGridQuestionData = () => ({
  id: null,
  columns: [],
  columnGroups: [],
  rows: [],
  rowGroups: [],
  cellLinks: {},
});

const useFormEditor = (formId, langId) => {
  const { setGlbForm } = useContext(GlbFormContext);
  const { setLoaderState } = useContext(LoaderContext);
  const { companyId } = useContext(CompanyContext);
  const latestSectionRequestRef = useRef(null);
  const latestFormRequestRef = useRef(null);
  const formDataRef = useRef(null);

  const [formdata, setFormData] = useState(null);
  const [formsectionsdata, setFormSectionsData] = useState(null);
  const [currentsection, setCurrentSection] = useState('');
  const [submitlanguage, setSubmitLanguage] = useState('');
  const [languages, setLanguages] = useState([]);
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubsection, setIsSubsection] = useState(false);
  const [isWithinSection, setIsWithinSection] = useState(true);
  const [fieldSectionId, setFieldSectionId] = useState(null);
  const [sidebarItemsReorderedOn, setSidebarItemsReorderedOn] = useState(0);

  // Section modal state
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionname, setSectionName] = useState('');
  const [sectiondesp, setSectionDesp] = useState('');
  const [scoringmethod, setScoringMethod] = useState('NONE');
  const [weightage, setWeightage] = useState('');
  const [uom_popup, setUomPopup] = useState('');
  const [uomRequiredPopup, setUomRequiredPopup] = useState(false);
  const [multiSupportEnabled, setMultiSupportEnabled] = useState(false);
  const [actionType, setActionType] = useState(1);
  const [selParentSection, setSelParentSection] = useState('');
  const [editedSectionId, setEditedSectionId] = useState(null);
  const [allSections, setAllSections] = useState([]);
  const [pageHelpText, setPageHelpText] = useState('');
  const [validated, setValidated] = useState(false);
  const [editEnable, setEditEnable] = useState(true);

  // Field editor state
  const [field, setField] = useState('');
  const [fieldeditid, setFieldEditId] = useState(null);
  const [selectedanswertype, setSelectedAnsType] = useState(null);
  const [showAnswerTypePicker, setShowAnswerTypePicker] = useState(false);
  const [isNewField, setIsNewField] = useState(false);
  const [choiceoptions, setChoiceOptions] = useState([{ ...OPTION_OBJ }]);
  const [optional, setOptional] = useState(true);
  const [scoreEnabled, setScoreEnabled] = useState(false);
  const [tabchoosed, setTabChoosed] = useState('general');
  const [helpText, setHelpText] = useState(Utils.getHelpTextPlaceHolder());
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [otherMandatory, setOtherMandatory] = useState(false);
  const [remarksArr, setRemarksArr] = useState([]);
  const [remarkTitle, setRemarkTitle] = useState('');
  const [remarkRequired, setRemarkRequired] = useState(false);
  const [allowRemark, setAllowRemark] = useState(false);
  const [allowComments, setAllowComments] = useState(false);
  const [uomRequired, setUomRequired] = useState(false);
  const [uomData, setUOMData] = useState({ relevantUoms: [], defaultUOM: '' });
  const [outputUnits, setOutputUnits] = useState([]);
  const [inputUnits, setInputUnits] = useState([]);
  const [extDependency, setExtDependency] = useState({ enabled: false, value: '' });
  const [extConfig, setExtConfig] = useState(null);
  const [emissionCats, setEmissionCats] = useState([]);
  const [allowFileUpload, setAllowFileUpload] = useState(false);
  const [fileMandatory, setFileMandatory] = useState(false);
  const [hisDataSupported, setHisDataSupported] = useState(false);
  const [optionSelectionValidation, setOptionSelectionValidation] = useState({
    operator: null, count: null, maximumCount: null, validationMessage: null,
  });
  const [gridData, setGridData] = useState({ columns: [], rowSets: [] });
  const [tableData, setTableData] = useState({ columns: [] });
  const [gridQuestionData, setGridQuestionData] = useState(defaultGridQuestionData);
  const [actionsarray, setActionsArray] = useState([{ ...ACTIONS_ARR_OBJ }]);

  // Delete confirm
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteItemSectionId, setDeleteItemSectionId] = useState(null);
  const [isDeleteSection, setIsDeleteSection] = useState(false);
  const [deleteSectionId, setDeleteSectionId] = useState('');
  const [updateform, setUpdateForm] = useState(false);
  const [showMoveFieldModal, setShowMoveFieldModal] = useState(false);
  const [fieldToMove, setFieldToMove] = useState(null);
  const [fieldFromSectionId, setFieldFromSectionId] = useState(null);
  const [moveTargetSectionId, setMoveTargetSectionId] = useState('');
  const [showMoveSectionModal, setShowMoveSectionModal] = useState(false);
  const [sectionToMove, setSectionToMove] = useState(null);
  const [sectionFromParentId, setSectionFromParentId] = useState(null);
  const [moveTargetParentSectionId, setMoveTargetParentSectionId] = useState('');
  const [insertContext, setInsertContext] = useState(null);
  const [lastCreatedItem, setLastCreatedItem] = useState(null);
  const [lastMovedItem, setLastMovedItem] = useState(null);

  // Unsaved-changes guard: any action that would discard an in-progress question
  // edit (switching fields, starting a new question, creating a section, dragging,
  // duplicating, deleting, moving, or navigating away) routes through guardAction
  // first. If there's nothing unsaved it runs immediately; otherwise it's stashed
  // here until the user responds to the Unsaved Changes prompt (save & continue /
  // discard / cancel).
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const pendingActionRef = useRef(null);

  const markUnsaved = useCallback(() => {
    setSaveStatus(SAVE_STATUS.UNSAVED);
  }, []);

  const hasUnsavedChanges = saveStatus === SAVE_STATUS.UNSAVED;

  const guardAction = useCallback((action) => {
    if (hasUnsavedChanges) {
      pendingActionRef.current = action;
      setShowUnsavedChangesModal(true);
      return;
    }
    action();
  }, [hasUnsavedChanges]);

  const cancelUnsavedChangesModal = useCallback(() => {
    pendingActionRef.current = null;
    setShowUnsavedChangesModal(false);
  }, []);

  const findSectionInTree = (sections, targetId) => {
    for (const section of sections || []) {
      if (section.id === targetId) return section;
      const found = findSectionInTree(section.subSections, targetId);
      if (found) return found;
    }
    return null;
  };

  // GET /forms/{formId} returns the full form tree (sections, subsections, and
  // questions) already hydrated, so the whole-form fetch is the single source of
  // truth here. Per-section fetches (getSectionDetails) are only issued when a
  // specific section is being edited or explicitly refreshed, never as a bulk
  // prefetch on load.
  const getFormDetails = useCallback(({ fetchFor, lang, sectionId, fieldId = null, onSuccess }) => {
    const requestId = {};
    latestFormRequestRef.current = requestId;
    FormEditService.getFormDetails(formId, lang || langId)
      .then((response) => {
        if (latestFormRequestRef.current !== requestId) return;
        setFormData(response.data);
        setGlbForm(response.data);
        setSubmitLanguage(response.data.selectedLanguage?.id);
        if (response.data.fields?.length > 0 && fetchFor === 'NO_SECTION') {
          setIsWithinSection(false);
          setFormSectionsData({ sectionAvailable: false, fields: response.data.fields });
          if (fieldId != null) {
            const match = response.data.fields.find((f) => f.id === fieldId);
            if (match) applyEditField(match, 0);
          }
          onSuccess?.();
        } else if (fetchFor === 'NO_SECTION') {
          fetchFor = 'INITIAL';
        }
        if (fetchFor === 'INITIAL') {
          if (response.data.fields?.length > 0) {
            setIsWithinSection(false);
            setFormSectionsData({ sectionAvailable: false, fields: response.data.fields });
          } else if (response.data.sections?.length > 0) {
            const sorted = [...response.data.sections].sort((a, b) =>
              (a.title || '').localeCompare(b.title || '')
            );
            setCurrentSection(sorted[0].id);
            setFormSectionsData({ ...sorted[0], sectionAvailable: true });
          }
        } else if (fetchFor === 'EDIT_SECTION') {
          const found = findSectionInTree(response.data.sections, sectionId);
          if (found) {
            setCurrentSection(sectionId);
            setFormSectionsData({ ...found, sectionAvailable: true });
            if (fieldId != null) {
              const match = found.fields?.find((f) => f.id === fieldId);
              if (match) applyEditField(match, sectionId);
            }
            onSuccess?.({ ...found, sectionAvailable: true });
          } else {
            // Section not present in the just-fetched tree (e.g. it was just
            // deleted) — fall back to a direct, targeted fetch.
            getSectionDetails(sectionId, fieldId, lang, onSuccess);
          }
        }
        setSaveStatus(SAVE_STATUS.SAVED);
      })
      .catch(() => {
        if (latestFormRequestRef.current === requestId) setSaveStatus(SAVE_STATUS.ERROR);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, langId, setGlbForm]);

  const getSectionDetails = useCallback((sectionId, fieldEditedId, lang, onSuccess) => {
    setCurrentSection(sectionId);
    const requestId = {};
    latestSectionRequestRef.current = requestId;
    setLoaderState?.({ display: true, type: 'TEXTUAL' });
    FormEditService.getSectionDetails(formId, sectionId, lang || langId)
      .then((response) => {
        if (latestSectionRequestRef.current === requestId) {
          setLoaderState?.({ display: false });
        }
        const responseData = { ...response.data, sectionAvailable: true };
        setFormSectionsData(responseData);
        const prevFormData = formDataRef.current;
        if (prevFormData) {
          const next = { ...prevFormData, sections: JSON.parse(JSON.stringify(prevFormData.sections || [])) };
          const patchFields = (sections) => {
            for (const section of sections || []) {
              if (section.id === sectionId) {
                section.fields = response.data.fields || [];
                return true;
              }
              if (patchFields(section.subSections)) return true;
            }
            return false;
          };
          patchFields(next.sections);
          setFormData(next);
          setGlbForm(next);
        }
        if (fieldEditedId != null) {
          const filtered = response.data.fields?.filter((x) => x.id === fieldEditedId);
          if (filtered?.[0]) applyEditField(filtered[0], sectionId);
        }
        onSuccess?.(responseData);
      })
      .catch(() => {
        if (latestSectionRequestRef.current === requestId) {
          setLoaderState?.({ display: false });
          toast.error('Failed to load section data', { position: toast.POSITION.TOP_RIGHT });
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, langId, setLoaderState]);

  const getFieldWithoutSection = useCallback(() => {
    if (!formdata || typeof formdata !== 'object') return;
    setIsWithinSection(false);
    setFormSectionsData({ sectionAvailable: false, fields: formdata.fields || [] });
  }, [formdata]);

  // Plain navigation into a Section/Subsection (sidebar clicks, selecting a field
  // that belongs to a different section) — the section is already fully hydrated
  // in formdata, so just read it from there instead of hitting GET
  // /sections/{sectionId} again. Falls back to a real fetch only if the section
  // can't be found locally (e.g. formdata hasn't loaded yet).
  const viewSection = useCallback((sectionId, fieldEditedId = null) => {
    const found = findSectionInTree(formdata?.sections, sectionId);
    if (!found) {
      getSectionDetails(sectionId, fieldEditedId, submitlanguage);
      return;
    }
    setCurrentSection(sectionId);
    setFormSectionsData({ ...found, sectionAvailable: true });
    if (fieldEditedId != null) {
      const match = found.fields?.find((f) => f.id === fieldEditedId);
      if (match) applyEditField(match, sectionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formdata, submitlanguage, getSectionDetails]);

  const resetFieldProperties = useCallback(() => {
    setTabChoosed('general');
    setFieldEditId(null);
    setSelectedAnsType(null);
    setIsNewField(false);
    setShowAnswerTypePicker(false);
    setChoiceOptions([{ ...OPTION_OBJ }]);
    setOptional(true);
    setScoreEnabled(false);
    setField('');
    setHelpText(Utils.getHelpTextPlaceHolder());
    setOtherChecked(false);
    setOtherText('');
    setRemarkTitle('');
    setRemarkRequired(false);
    setAllowRemark(false);
    setOtherMandatory(false);
    setRemarksArr([]);
    setUomRequired(false);
    setUOMData({ relevantUoms: [], defaultUOM: '' });
    setExtDependency({ enabled: false, value: '' });
    setExtConfig(null);
    setAllowFileUpload(false);
    setAllowComments(false);
    setOptionSelectionValidation({ operator: null, count: null, maximumCount: null, validationMessage: null });
    setFileMandatory(false);
    setHisDataSupported(false);
    setGridData({ columns: [], rowSets: [] });
    setTableData({ columns: [] });
    setGridQuestionData(defaultGridQuestionData());
    setInsertContext(null);
    setFieldSectionId(null);
  }, []);

  useEffect(() => {
    formDataRef.current = formdata;
  }, [formdata]);

  const findSectionIdForField = useCallback((fieldId, data = formdata) => {
    if (!fieldId || !data) return null;
    if (data.fields?.some((f) => f.id === fieldId)) return 0;
    const walk = (sections) => {
      for (const section of sections || []) {
        if (section.fields?.some((f) => f.id === fieldId)) return section.id;
        const sub = walk(section.subSections);
        if (sub != null) return sub;
      }
      return null;
    };
    return walk(data.sections);
  }, [formdata]);

  const applyEditField = useCallback((item, knownSectionId) => {
    setShowAnswerTypePicker(false);
    resetFieldProperties();
    if ([QuestionType.MULTI_SELECT_DROP_DOWN, QuestionType.DROP_DOWN, QuestionType.RADIO, QuestionType.MULTIPLE_CHOICE].includes(item.type)) {
      setChoiceOptions(item.options || [{ ...OPTION_OBJ }]);
    }
    if (item.type === QuestionType.RADIO_GRID && item.gridConfig) {
      setGridData(mapGridConfigToEditorData(item.gridConfig));
    }
    if (item.type === QuestionType.TABLE && item.tableConfig) {
      setTableData(mapTableConfigToEditorData(item.tableConfig));
    }
    if (item.type === QuestionType.GRID && item.gridQuestionConfig) {
      setGridQuestionData(mapGridQuestionConfigToEditorData(item.gridQuestionConfig));
    }
    setFieldEditId(item.id);
    setField(item.name);
    setSelectedAnsType(item.type);
    setIsNewField(false);
    setOptional(item.optional);
    setScoreEnabled(item.scoreEnabled);
    setHelpText(item.fieldHelpText);
    setOtherChecked(item.allowOtherOption);
    setOtherText(item.otherTextLabel);
    setRemarkTitle(item.remarkLabel);
    setRemarkRequired(item.remarkMandatory);
    setAllowRemark(item?.allowRemark);
    setOtherMandatory(item?.otherFieldValidation?.mandatory);
    setRemarksArr(item.options?.filter((x) => x.showRemarks) || []);
    setUomRequired(item.uomRequired);
    setUOMData({ relevantUoms: item.relevantUoms ?? [], defaultUOM: item.defaultUOM });
    setExtDependency({ enabled: item.extDependency !== null, value: item.extDependency ?? '' });
    setExtConfig(item.extConfig ?? null);
    setAllowFileUpload(item.allowFileUpload);
    setFileMandatory(item.fileMandatory);
    setAllowComments(item.allowRemark);
    setHisDataSupported(item.historicalDataSupported);
    const resolvedSectionId = knownSectionId ?? findSectionIdForField(item.id);
    setFieldSectionId(resolvedSectionId != null ? resolvedSectionId : (formsectionsdata?.sectionAvailable ? formsectionsdata?.id : 0));
    setIsWithinSection(resolvedSectionId != null ? resolvedSectionId > 0 : !!formsectionsdata?.sectionAvailable);
    setOptionSelectionValidation(item.optionSelectionValidation || { operator: null, count: null, maximumCount: null, validationMessage: null });
    setTabChoosed('general');
  }, [formsectionsdata, resetFieldProperties, findSectionIdForField]);

  const editField = useCallback((item, knownSectionId) => {
    guardAction(() => {
      const sectionId = knownSectionId != null && knownSectionId > 0 ? knownSectionId : null;
      setLoaderState?.({ display: true, type: 'TEXTUAL' });
      FormEditService.getFieldDetails(formId, item.id, sectionId)
        .then((response) => {
          setLoaderState?.({ display: false });
          applyEditField(response.data, knownSectionId);
        })
        .catch(() => {
          setLoaderState?.({ display: false });
          toast.error('Failed to fetch question details');
        });
    });
  }, [guardAction, applyEditField, formId, setLoaderState]);

  const selectQuestion = useCallback((item, knownSectionId) => {
    guardAction(() => {
      if (fieldeditid === item.id) return;
      editField(item, knownSectionId);
    });
  }, [guardAction, fieldeditid, editField]);

  // Patches a field's properties directly in formdata/formsectionsdata — the
  // trees QuestionCard actually renders from — without a network round trip.
  // Used so a toggle like Required can update the canvas's "*" indicator the
  // instant it's clicked, instead of waiting on a save + full section refetch.
  const patchFieldInTrees = useCallback((fieldId, updates) => {
    if (fieldId == null) return;
    const patchArray = (fields) => {
      if (!fields) return null;
      const idx = fields.findIndex((f) => f.id === fieldId);
      if (idx < 0) return null;
      const next = [...fields];
      next[idx] = { ...next[idx], ...updates };
      return next;
    };

    setFormSectionsData((prev) => {
      const nextFields = patchArray(prev?.fields);
      return nextFields ? { ...prev, fields: nextFields } : prev;
    });

    setFormData((prev) => {
      if (!prev) return prev;
      const nextRootFields = patchArray(prev.fields);

      const patchSections = (sections) => {
        if (!sections) return sections;
        let sectionsChanged = false;
        const next = sections.map((section) => {
          const nextFields = patchArray(section.fields);
          const nextSub = patchSections(section.subSections);
          if (nextFields || nextSub !== section.subSections) {
            sectionsChanged = true;
            return { ...section, fields: nextFields || section.fields, subSections: nextSub };
          }
          return section;
        });
        return sectionsChanged ? next : sections;
      };

      const nextSections = patchSections(prev.sections);
      if (!nextRootFields && nextSections === prev.sections) return prev;

      const next = { ...prev, fields: nextRootFields || prev.fields, sections: nextSections };
      setGlbForm(next);
      return next;
    });
  }, [setGlbForm]);

  const handleSaveField = useCallback((isEdit, onSaved) => {
    setSaveStatus(SAVE_STATUS.SAVING);
    const activeTab = tabchoosed;
    // The full-form/section refetch below is a summary view and isn't
    // guaranteed to echo back every validation-related property for a field.
    // Capture exactly what we're about to send so we can re-assert it after
    // the refresh, instead of letting a possibly-incomplete refetch silently
    // reset Required (or its dependent mandatory flags) back to a default.
    const savedOptional = optional;
    const savedRemarkRequired = remarkRequired;
    const savedFileMandatory = fileMandatory;
    const params = buildFieldPayload({
      field, selectedAnswerType: selectedanswertype, optional, scoreEnabled, helpText,
      submitLanguage: submitlanguage, choiceOptions: choiceoptions, remarksArr, remarkTitle, remarkRequired,
      allowComments, otherChecked, otherText, otherMandatory, uomRequired, uomData,
      extDependency, extConfig, allowFileUpload, fileMandatory, hisDataSupported,
      gridData, tableData, gridQuestionData, optionSelectionValidation, fieldEditId: fieldeditid,
    });
    const pendingInsert = insertContext?.kind === 'field' ? insertContext : null;
    const sectionId = pendingInsert?.sectionId ?? (fieldeditid ? fieldSectionId : formsectionsdata?.id);
    const withinSection = pendingInsert ? pendingInsert.sectionId > 0 : isWithinSection;
    const onSuccess = () => {
      setSaveStatus(SAVE_STATUS.SAVED);
      toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
    };
    // onSaved (e.g. a guarded action waiting on this save) must run after the
    // post-save refresh's own re-select of the saved field, not before it —
    // otherwise that re-select would clobber whatever onSaved just did.
    const refreshAfterFieldCreate = (targetSectionId, editedFieldId) => {
      const afterRefresh = () => {
        setOptional(savedOptional);
        setRemarkRequired(savedRemarkRequired);
        setFileMandatory(savedFileMandatory);
        patchFieldInTrees(editedFieldId, {
          optional: savedOptional,
          remarkMandatory: savedRemarkRequired,
          fileMandatory: savedFileMandatory,
        });
        setTabChoosed(activeTab);
        onSaved?.();
      };
      if (targetSectionId > 0) {
        getFormDetails({
          fetchFor: 'EDIT_SECTION', lang: submitlanguage, sectionId: targetSectionId,
          fieldId: editedFieldId, onSuccess: afterRefresh,
        });
      } else {
        getFormDetails({
          fetchFor: 'NO_SECTION', lang: submitlanguage,
          fieldId: editedFieldId, onSuccess: afterRefresh,
        });
      }
    };
    const finalizeFieldCreate = (res, targetSectionId) => {
      if (res.status !== 201 && res.status !== 200) return;
      const newFieldId = res.data?.id;
      const editedFieldId = fieldeditid || newFieldId;
      if (!isEdit && !fieldeditid && newFieldId != null) {
        setLastCreatedItem({ type: 'field', id: newFieldId, sectionId: targetSectionId });
      }
      if (pendingInsert && newFieldId != null) {
        const prevFieldId = pendingInsert.prevFieldId ?? 0;
        FormEditService.reorderField(formId, newFieldId, { sectionId: targetSectionId, prevFieldId })
          .then(() => {
            setSidebarItemsReorderedOn(Date.now());
            refreshAfterFieldCreate(targetSectionId, editedFieldId);
            onSuccess();
          })
          .catch(() => setSaveStatus(SAVE_STATUS.ERROR));
        return;
      }
      if (fieldeditid) {
        // Editing a field that already exists: `params` is exactly what we
        // just told the backend to persist, scoped to this one field's id —
        // patch it in locally instead of re-fetching (and re-rendering) the
        // whole form/section, which would needlessly touch every other
        // question's data along the way. Pull only option/grid ids from the
        // response (server-assigned on save), never the validation flags —
        // those stay whatever we just sent, not whatever a summary DTO says.
        patchFieldInTrees(fieldeditid, {
          ...params,
          ...(res.data?.options ? { options: res.data.options } : {}),
          ...(res.data?.gridConfig ? { gridConfig: res.data.gridConfig } : {}),
          optional: savedOptional,
          remarkMandatory: savedRemarkRequired,
          fileMandatory: savedFileMandatory,
        });
        setOptional(savedOptional);
        setRemarkRequired(savedRemarkRequired);
        setFileMandatory(savedFileMandatory);
        setTabChoosed(activeTab);
        onSaved?.();
        onSuccess();
        return;
      }
      refreshAfterFieldCreate(targetSectionId, editedFieldId);
      onSuccess();
    };
    if (withinSection && sectionId) {
      FormEditService.createField(formId, sectionId, params)
        .then((res) => finalizeFieldCreate(res, sectionId))
        .catch(() => setSaveStatus(SAVE_STATUS.ERROR));
    } else {
      FormEditService.createUpdateFieldWithoutSection(formId, params, isEdit || !!fieldeditid)
        .then((res) => finalizeFieldCreate(res, 0))
        .catch(() => setSaveStatus(SAVE_STATUS.ERROR));
    }
  }, [field, selectedanswertype, optional, scoreEnabled, helpText, submitlanguage, choiceoptions,
    remarksArr, remarkTitle, remarkRequired, allowComments, otherChecked, otherText, otherMandatory,
    uomRequired, uomData, extDependency, extConfig, allowFileUpload, fileMandatory, hisDataSupported,
    gridData, tableData, gridQuestionData, optionSelectionValidation, fieldeditid, isWithinSection,
    fieldSectionId, formsectionsdata, formId, getFormDetails, insertContext, tabchoosed, patchFieldInTrees]);

  // Scoped save for the Required toggle: persists just this field through the
  // same create/update call the full Save uses, but — since the UI is already
  // correct from the optimistic patchFieldInTrees call above — skips the
  // post-save section/form refetch, and rolls the optimistic change back if the
  // save actually fails so the UI never drifts from the backend.
  const saveRequiredToggle = useCallback((newOptional, clearMandatoryFlags, previous) => {
    const params = buildFieldPayload({
      field, selectedAnswerType: selectedanswertype, scoreEnabled, helpText,
      submitLanguage: submitlanguage, choiceOptions: choiceoptions, remarksArr, remarkTitle,
      allowComments, otherChecked, otherText, otherMandatory, uomRequired, uomData,
      extDependency, extConfig, allowFileUpload, hisDataSupported,
      gridData, optionSelectionValidation, fieldEditId: fieldeditid,
      optional: newOptional,
      remarkRequired: clearMandatoryFlags ? false : remarkRequired,
      fileMandatory: clearMandatoryFlags ? false : fileMandatory,
    });

    const pendingInsert = insertContext?.kind === 'field' ? insertContext : null;
    const sectionId = pendingInsert?.sectionId ?? fieldSectionId;
    const withinSection = pendingInsert ? pendingInsert.sectionId > 0 : isWithinSection;
    const fieldId = fieldeditid;

    const rollback = () => {
      setOptional(previous.optional);
      if (clearMandatoryFlags) {
        setRemarkRequired(previous.remarkRequired);
        setFileMandatory(previous.fileMandatory);
      }
      patchFieldInTrees(fieldId, {
        optional: previous.optional,
        ...(clearMandatoryFlags
          ? { remarkMandatory: previous.remarkRequired, fileMandatory: previous.fileMandatory }
          : {}),
      });
      setSaveStatus(SAVE_STATUS.ERROR);
      toast.error('Failed to save Required setting', { position: toast.POSITION.TOP_RIGHT });
    };

    setSaveStatus(SAVE_STATUS.SAVING);
    const request = withinSection && sectionId
      ? FormEditService.createField(formId, sectionId, params)
      : FormEditService.createUpdateFieldWithoutSection(formId, params, true);

    request
      .then((res) => {
        if (res.status !== 200 && res.status !== 201) { rollback(); return; }
        setSaveStatus(SAVE_STATUS.SAVED);
        toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
      })
      .catch(rollback);
  }, [
    field, selectedanswertype, scoreEnabled, helpText, submitlanguage, choiceoptions, remarksArr, remarkTitle,
    remarkRequired, allowComments, otherChecked, otherText, otherMandatory, uomRequired, uomData, extDependency,
    extConfig, allowFileUpload, fileMandatory, hisDataSupported, gridData, optionSelectionValidation, fieldeditid,
    insertContext, fieldSectionId, isWithinSection, formId, patchFieldInTrees,
  ]);

  const confirmSaveAndContinue = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setShowUnsavedChangesModal(false);
    handleSaveField(!!fieldeditid, () => action?.());
  }, [handleSaveField, fieldeditid]);

  const confirmDiscardAndContinue = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setShowUnsavedChangesModal(false);
    resetFieldProperties();
    setSaveStatus(SAVE_STATUS.SAVED);
    action?.();
  }, [resetFieldProperties]);

  const chooseAnswerType = useCallback((type) => {
    if ([QuestionType.RADIO, QuestionType.RADIO_GRID, QuestionType.MULTI_SELECT_DROP_DOWN, QuestionType.DROP_DOWN, QuestionType.MULTIPLE_CHOICE].includes(type)) {
      setScoreEnabled(true);
    }
    setSelectedAnsType(type);
    setShowAnswerTypePicker(false);
    setIsNewField(true);
    setFieldEditId(null);
    if (type === QuestionType.NUMERIC_VALUE) setHisDataSupported(formdata?.historicalDataSupported ?? false);
    if (type === QuestionType.RADIO_GRID) setGridData({ columns: [], rowSets: [] });
    if (type === QuestionType.TABLE) setTableData({ columns: [] });
    if (type === QuestionType.GRID) setGridQuestionData(defaultGridQuestionData());
    markUnsaved();
  }, [formdata, markUnsaved]);

  const applyStartNewQuestion = useCallback((withinSection = true, context = null) => {
    resetFieldProperties();
    setIsWithinSection(withinSection);
    if (context) setInsertContext(context);
    setShowAnswerTypePicker(true);
  }, [resetFieldProperties]);

  const startNewQuestion = useCallback((withinSection = true, context = null) => {
    guardAction(() => applyStartNewQuestion(withinSection, context));
  }, [guardAction, applyStartNewQuestion]);

  const closeQuestionTypePicker = useCallback(() => {
    resetFieldProperties();
  }, [resetFieldProperties]);

  const insertQuestionAt = useCallback((sectionId, prevFieldId) => {
    const withinSection = sectionId > 0;
    if (withinSection) {
      getSectionDetails(sectionId, null, submitlanguage);
    } else {
      getFieldWithoutSection();
    }
    startNewQuestion(withinSection, { kind: 'field', sectionId, prevFieldId });
  }, [getSectionDetails, getFieldWithoutSection, startNewQuestion, submitlanguage]);

  const applyDeleteField = useCallback((item, sectionId = null) => {
    setDeleteItem(item);
    setDeleteItemSectionId(sectionId);
    setConfirmMessage('Are you sure want to delete this field?');
    setIsConfirmModal(true);
  }, []);

  const deleteField = useCallback((item, sectionId = null) => {
    guardAction(() => applyDeleteField(item, sectionId));
  }, [guardAction, applyDeleteField]);

  const confirmDeleteField = useCallback((confirmed) => {
    setIsConfirmModal(false);
    if (!confirmed || !deleteItem) {
      setDeleteItemSectionId(null);
      return;
    }

    const resolvedSectionId = deleteItemSectionId != null
      ? deleteItemSectionId
      : (findSectionIdForField(deleteItem.id) ?? (formsectionsdata?.sectionAvailable ? formsectionsdata?.id : 0));
    const apiSectionId = resolvedSectionId > 0 ? resolvedSectionId : '';

    FormEditService.deleteField(formId, deleteItem.id, apiSectionId)
      .then((res) => {
        if (res.status === 204) {
          setDeleteItem(null);
          setDeleteItemSectionId(null);
          resetFieldProperties();
          if (resolvedSectionId > 0) {
            getFormDetails({ fetchFor: 'EDIT_SECTION', lang: submitlanguage, sectionId: resolvedSectionId });
          } else {
            getFormDetails({ fetchFor: 'NO_SECTION', lang: submitlanguage });
          }
          toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((err) => {
        setSaveStatus(SAVE_STATUS.ERROR);
        toast.error(err?.message || 'Failed to delete question', { position: toast.POSITION.TOP_RIGHT });
      });
  }, [deleteItem, deleteItemSectionId, findSectionIdForField, formsectionsdata, formId, submitlanguage,
    getFormDetails, resetFieldProperties]);

  const applyDeleteSection = useCallback((secId) => {
    setDeleteSectionId(secId);
    setIsDeleteSection(true);
  }, []);

  const deleteSection = useCallback((secId) => {
    guardAction(() => applyDeleteSection(secId));
  }, [guardAction, applyDeleteSection]);

  const confirmDeleteSection = useCallback((confirmed, delType) => {
    setIsDeleteSection(false);
    if (!confirmed) return;
    FormEditService.deleteSection(formId, deleteSectionId, delType)
      .then((res) => {
        if (res.status === 204) {
          setDeleteSectionId('');
          setFormSectionsData(null);
          getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage });
          toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
        }
      });
  }, [formId, deleteSectionId, submitlanguage, getFormDetails]);

  const reorderFieldApi = useCallback((fieldId, previousFieldId, sectionId, prevFormData) => {
    FormEditService.reorderField(formId, fieldId, { sectionId, prevFieldId: previousFieldId })
      .then(() => {
        if (sectionId === 0) {
          getFormDetails({ fetchFor: 'NO_SECTION', lang: submitlanguage });
        } else if (currentsection === sectionId) {
          getFormDetails({ fetchFor: 'EDIT_SECTION', lang: submitlanguage, sectionId });
        } else {
          getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage });
        }
      })
      .catch((err) => {
        if (prevFormData) {
          setFormData(prevFormData);
          setGlbForm(prevFormData);
        }
        toast.error(err.message, { position: toast.POSITION.TOP_RIGHT });
      });
  }, [formId, submitlanguage, currentsection, getFormDetails, setGlbForm]);

  const reorderSectionApi = useCallback((sectionIdToMove, sectionId, previousSectionId, prevFormData) => {
    return FormEditService.reorderSection(formId, sectionIdToMove, { sectionId, prevSectionId: previousSectionId })
      .then(() => getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage }))
      .catch((err) => {
        if (prevFormData) {
          setFormData(prevFormData);
          setGlbForm(prevFormData);
        }
        toast.error(err.message, { position: toast.POSITION.TOP_RIGHT });
      });
  }, [formId, submitlanguage, getFormDetails, setGlbForm]);

  const applyHandleFieldDrop = useCallback((droppedField, droppedOver, sectionId) => {
    const prevFormData = formdata;
    const data = { ...formdata, fields: [...(formdata.fields || [])], sections: JSON.parse(JSON.stringify(formdata.sections || [])) };
    const insertAsFirst = droppedOver.id === 0;

    // Always remove the dragged field from wherever it currently lives — root
    // fields or any Section/Subsection — before inserting it at the new
    // position. Previously this only happened when the insertion target was
    // itself inside a section; dropping a field FROM a section INTO the root
    // fields list left a stale duplicate behind in its old section until the
    // next full refetch.
    const deleteIndex = data.fields?.findIndex((f) => f.id === droppedField.id);
    if (deleteIndex >= 0) {
      data.fields.splice(deleteIndex, 1);
    } else {
      const removeWalk = (sections) => {
        for (const section of sections || []) {
          const dIdx = section.fields?.findIndex((f) => f.id === droppedField.id);
          if (dIdx >= 0) {
            section.fields.splice(dIdx, 1);
            return true;
          }
          if (section.subSections?.length && removeWalk(section.subSections)) return true;
        }
        return false;
      };
      removeWalk(data.sections);
    }

    if (sectionId === 0) {
      const insertIndex = insertAsFirst ? 0 : data.fields?.findIndex((f) => f.id === droppedOver.id);
      if (insertAsFirst) data.fields.splice(0, 0, droppedField);
      else if (insertIndex >= 0) data.fields.splice(insertIndex + 1, 0, droppedField);
      else data.fields.push(droppedField);
    } else {
      const insertWalk = (sections) => {
        for (const section of sections || []) {
          if (section.id === sectionId) {
            section.fields = section.fields || [];
            const iIdx = insertAsFirst ? 0 : section.fields.findIndex((f) => f.id === droppedOver.id);
            if (insertAsFirst) section.fields.splice(0, 0, droppedField);
            else if (iIdx >= 0) section.fields.splice(iIdx + 1, 0, droppedField);
            else section.fields.push(droppedField);
            return true;
          }
          if (section.subSections?.length && insertWalk(section.subSections)) return true;
        }
        return false;
      };
      insertWalk(data.sections);
    }
    setFormData(data);
    setGlbForm(data);
    if (sectionId === 0 && !formsectionsdata?.sectionAvailable) {
      setFormSectionsData({ sectionAvailable: false, fields: data.fields || [] });
    } else if (formsectionsdata?.id === sectionId) {
      const sectionFields = findFieldsInSections(data.sections, sectionId) || data.fields;
      setFormSectionsData((prev) => ({ ...prev, fields: sectionFields }));
    }
    setSidebarItemsReorderedOn(Date.now());
    setLastMovedItem({ type: 'field', id: droppedField.id, sectionId });
    reorderFieldApi(droppedField.id, droppedOver.id, sectionId, prevFormData);
  }, [formdata, formsectionsdata, setGlbForm, reorderFieldApi]);

  const handleFieldDrop = useCallback((droppedField, droppedOver, sectionId) => {
    guardAction(() => applyHandleFieldDrop(droppedField, droppedOver, sectionId));
  }, [guardAction, applyHandleFieldDrop]);

  const findFieldsInSections = (sections, sectionId) => {
    for (const section of sections || []) {
      if (section.id === sectionId) return section.fields;
      const sub = findFieldsInSections(section.subSections, sectionId);
      if (sub) return sub;
    }
    return null;
  };

  const findParentSectionId = (sectionId, sections = formdata?.sections) => {
    for (const section of sections || []) {
      if (section.subSections?.some((s) => s.id === sectionId)) return section.id;
      const parentId = findParentSectionId(sectionId, section.subSections);
      if (parentId) return parentId;
    }
    return null;
  };

  const applyHandleSectionDrop = useCallback((droppedSection, droppedOverSection, parentSectionId = 0) => {
    if (!droppedSection || !droppedOverSection || droppedSection.id === droppedOverSection.id) return;

    const prevFormData = formdata;
    const data = { ...formdata, sections: JSON.parse(JSON.stringify(formdata.sections || [])) };

    // Resolve the anchor sibling ("place after this id") from the moved item's
    // final position in the list, not the raw hovered id — otherwise upward
    // drags report the wrong anchor (the API would place the section one slot
    // off from where it was optimistically shown).
    let resolvedPrevSectionId = droppedOverSection.id;

    const reorderList = (list) => {
      const fromIdx = list.findIndex((s) => s.id === droppedSection.id);
      const toIdx = list.findIndex((s) => s.id === droppedOverSection.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const [moved] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, moved);
      const newIdx = list.findIndex((s) => s.id === moved.id);
      resolvedPrevSectionId = newIdx > 0 ? list[newIdx - 1].id : 0;
    };

    if (!parentSectionId) {
      reorderList(data.sections);
    } else {
      const walk = (sections) => {
        for (const section of sections || []) {
          if (section.id === parentSectionId && section.subSections) {
            reorderList(section.subSections);
            return true;
          }
          if (section.subSections?.length && walk(section.subSections)) return true;
        }
        return false;
      };
      walk(data.sections);
    }

    setFormData(data);
    setGlbForm(data);
    setSidebarItemsReorderedOn(Date.now());
    setLastMovedItem({ type: 'section', id: droppedSection.id, parentSectionId });
    reorderSectionApi(droppedSection.id, parentSectionId, resolvedPrevSectionId, prevFormData);
  }, [formdata, setGlbForm, reorderSectionApi]);

  const handleSectionDrop = useCallback((droppedSection, droppedOverSection, parentSectionId = 0) => {
    guardAction(() => applyHandleSectionDrop(droppedSection, droppedOverSection, parentSectionId));
  }, [guardAction, applyHandleSectionDrop]);

  // Move a subsection to a different parent Section, inserting it at an arbitrary
  // position. Unlike confirmMoveSectionToParent (used by the "Move to..." modal, which
  // always unshifts to the front), this supports inserting after a specific sibling —
  // used by cross-container drag-and-drop.
  const applyMoveSectionToParent = useCallback((sectionToMove, fromParentId, targetParentId, targetPrevSectionId) => {
    if (!sectionToMove || !targetParentId) return;

    const prevFormData = formdata;
    const data = { ...formdata, sections: JSON.parse(JSON.stringify(formdata.sections || [])) };

    const removeFromParent = (sections) => {
      for (const section of sections || []) {
        if (section.id === fromParentId && section.subSections?.length) {
          const idx = section.subSections.findIndex((s) => s.id === sectionToMove.id);
          if (idx >= 0) return section.subSections.splice(idx, 1)[0];
        }
        if (section.subSections?.length) {
          const removed = removeFromParent(section.subSections);
          if (removed) return removed;
        }
      }
      return null;
    };

    const insertIntoParent = (sections, removedSection) => {
      for (const section of sections || []) {
        if (section.id === targetParentId) {
          section.subSections = section.subSections || [];
          if (!targetPrevSectionId) {
            section.subSections.unshift(removedSection);
          } else {
            const idx = section.subSections.findIndex((s) => s.id === targetPrevSectionId);
            section.subSections.splice(idx >= 0 ? idx + 1 : section.subSections.length, 0, removedSection);
          }
          return true;
        }
        if (section.subSections?.length && insertIntoParent(section.subSections, removedSection)) return true;
      }
      return false;
    };

    const removedSection = removeFromParent(data.sections);
    if (!removedSection) return;
    insertIntoParent(data.sections, removedSection);

    setFormData(data);
    setGlbForm(data);
    setSidebarItemsReorderedOn(Date.now());
    setLastMovedItem({ type: 'section', id: sectionToMove.id, parentSectionId: targetParentId });

    if (formsectionsdata?.id === sectionToMove.id) {
      getSectionDetails(sectionToMove.id, null, submitlanguage);
    }

    reorderSectionApi(sectionToMove.id, targetParentId, targetPrevSectionId || 0, prevFormData);
  }, [formdata, formsectionsdata, setGlbForm, getSectionDetails, submitlanguage, reorderSectionApi]);

  const moveSectionToParent = useCallback((sectionToMove, fromParentId, targetParentId, targetPrevSectionId) => {
    guardAction(() => applyMoveSectionToParent(sectionToMove, fromParentId, targetParentId, targetPrevSectionId));
  }, [guardAction, applyMoveSectionToParent]);

  const applyOpenSectionModal = useCallback((type = 1, context = null) => {
    setEditEnable(false);
    setValidated(false);
    setSectionName('');
    setSectionDesp('');
    setEditedSectionId(null);
    setWeightage('');
    setScoringMethod('NONE');
    setUomRequiredPopup(false);
    setUomPopup('');
    setActionType(type);
    const parentFromContext = context?.parentSectionId;
    setSelParentSection(
      type === 2
        ? String(parentFromContext > 0 ? parentFromContext : (currentsection || ''))
        : '',
    );
    setInsertContext(context?.kind === 'section' ? context : null);
    setShowSectionModal(true);
  }, [currentsection]);

  const openSectionModal = useCallback((type = 1, context = null) => {
    guardAction(() => applyOpenSectionModal(type, context));
  }, [guardAction, applyOpenSectionModal]);

  const editSectionModal = useCallback((sectionData = formsectionsdata, isSub = isSubsection) => {
    setEditedSectionId(sectionData.id);
    setSectionName(sectionData.title);
    setSectionDesp(sectionData.description);
    setScoringMethod(sectionData.scoringMethod || 'NONE');
    setWeightage(sectionData.weightage || '');
    setUomPopup(sectionData.uom || '');
    setUomRequiredPopup(!!sectionData.uom);
    setMultiSupportEnabled(!!sectionData.multiSupportEnabled);
    setActionType(isSub ? 2 : 1);
    setSelParentSection(isSub ? String(sectionData.parentSection?.id ?? '') : '');
    setValidated(false);
    setEditEnable(true);
    setShowSectionModal(true);
  }, [formsectionsdata, isSubsection]);

  const handleCreateSectionForm = useCallback((e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }
    if (actionType === 2) {
      const parentId = parseInt(selParentSection, 10);
      if (!selParentSection || Number.isNaN(parentId)) {
        setValidated(true);
        toast.error('Please select a parent section', { position: toast.POSITION.TOP_RIGHT });
        return;
      }
    }
    setSaveStatus(SAVE_STATUS.SAVING);
    const params = {
      title: sectionname,
      description: sectiondesp,
      layout: 'INLINE',
      displayTitle: true,
      sortOrder: 100,
      pageHelpText,
    };
    if (actionType === 2) params.parentSection = { id: parseInt(selParentSection, 10) };
    if (formdata?.formType === ASSESSMENTFORMTXT || formdata?.formType === GENERAL_FORM) {
      params.weightage = weightage;
      params.scoringMethod = scoringmethod;
    }
    if (submitlanguage) params.selectedLanguage = { id: submitlanguage };
    if (uomRequiredPopup) params.uom = uom_popup;
    if (multiSupportEnabled) params.multiSupportEnabled = multiSupportEnabled;

    const pendingInsert = insertContext?.kind === 'section' ? insertContext : null;

    const finishSectionCreate = (res) => {
      setInsertContext(null);
      const parentId = actionType === 2 ? parseInt(selParentSection, 10) : null;
      const refreshSectionId = editedSectionId > 0
        ? editedSectionId
        : (actionType === 2 && parentId > 0 ? parentId : res.data?.id);

      if (refreshSectionId) {
        getFormDetails({ fetchFor: 'EDIT_SECTION', lang: submitlanguage, sectionId: refreshSectionId });
      } else {
        getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage });
      }
      if (editedSectionId == null && res.data?.id != null) {
        setLastCreatedItem({ type: 'section', id: res.data.id, parentSectionId: parentId > 0 ? parentId : 0 });
      }
      setShowSectionModal(false);
      setSaveStatus(SAVE_STATUS.SAVED);
      toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
    };

    FormEditService.createSection(formId, params, editedSectionId)
      .then(async (res) => {
        if (res.status !== 200 && res.status !== 201) return;
        const newSectionId = res.data?.id;
        if (pendingInsert && newSectionId != null && editedSectionId == null) {
          const parentId = pendingInsert.parentSectionId ?? 0;
          const prevSectionId = pendingInsert.prevSectionId ?? 0;
          try {
            await FormEditService.reorderSection(formId, newSectionId, {
              sectionId: parentId,
              prevSectionId: prevSectionId,
            });
            setSidebarItemsReorderedOn(Date.now());
          } catch (err) {
            toast.error(
              err?.message || 'Subsection created but could not be repositioned',
              { position: toast.POSITION.TOP_RIGHT },
            );
          }
        }
        finishSectionCreate(res);
      })
      .catch((err) => {
        setSaveStatus(SAVE_STATUS.ERROR);
        toast.error(err?.message || 'Failed to save section', { position: toast.POSITION.TOP_RIGHT });
      });
    setValidated(true);
  }, [sectionname, sectiondesp, pageHelpText, actionType, selParentSection, formdata,
    weightage, scoringmethod, submitlanguage, uomRequiredPopup, uom_popup, multiSupportEnabled,
    editedSectionId, formId, getFormDetails, insertContext]);

  const closeSectionModal = useCallback(() => {
    setShowSectionModal(false);
    setInsertContext(null);
  }, []);

  const getAllSections = useCallback(() => {
    FormEditService.getAllSections(formId, submitlanguage || 1)
      .then((res) => setAllSections(res.data))
      .catch(() => {});
  }, [formId, submitlanguage]);

  const getActionDetails = useCallback(() => {
    if (!fieldeditid) return;
    setActionsArray([{ ...ACTIONS_ARR_OBJ }]);
    FormEditService.getActions(formId, fieldeditid)
      .then((res) => { if (res.data?.length > 0) setActionsArray([...res.data]); })
      .catch(() => {});
  }, [formId, fieldeditid]);

  const handleSaveActions = useCallback(() => {
    FormEditService.createActions(formId, fieldeditid, { actions: actionsarray })
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
        }
      });
  }, [formId, fieldeditid, actionsarray]);

  const loadUnitsOfMeasure = useCallback(() => {
    FormService.getUnitsOfMeasure(companyId).then((res) => {
      if (!res.data?.length) return;
      const outputUnitsMap = new Map();
      const inputUnitsMap = new Map();
      res.data.forEach((el) => {
        const existingOutput = outputUnitsMap.get(el.outputUnit);
        const isExactLabelMatch = el.outputUnitName === el.outputUnit;
        if (!existingOutput || (isExactLabelMatch && existingOutput.label !== existingOutput.value)) {
          outputUnitsMap.set(el.outputUnit, { id: el.id, label: el.outputUnitName, value: el.outputUnit });
        }
        if (!inputUnitsMap.has(el.inputUnit)) {
          inputUnitsMap.set(el.inputUnit, { id: el.id, label: el.inputUnitName, value: el.inputUnit });
        }
      });
      setOutputUnits([...outputUnitsMap.values()]);
      setInputUnits([...inputUnitsMap.values()]);
    });
  }, [companyId]);

  const loadEmissionCats = useCallback((scope) => {
    CarbonEmissionService.getCategories(scope).then((res) => {
      if (res.status === 200) setEmissionCats(res.data.data);
    });
  }, []);

  const applyDuplicateField = useCallback((item, knownSectionId = null) => {
    if (!item?.id) return;
    const sectionId = knownSectionId != null
      ? knownSectionId
      : (findSectionIdForField(item.id) ?? (formsectionsdata?.sectionAvailable ? formsectionsdata?.id : 0));
    setSaveStatus(SAVE_STATUS.SAVING);

    const fetchNewField = (newFieldId) => {
      if (sectionId) {
        return FormEditService.getSectionDetails(formId, sectionId, submitlanguage)
          .then((res) => res.data?.fields?.find((f) => f.id === newFieldId) ?? null);
      }
      return FormEditService.getFormDetails(formId, submitlanguage)
        .then((res) => res.data?.fields?.find((f) => f.id === newFieldId) ?? null);
    };

    const refreshForm = () => {
      if (sectionId > 0) {
        getFormDetails({ fetchFor: 'EDIT_SECTION', lang: submitlanguage, sectionId });
      } else {
        getFormDetails({ fetchFor: 'NO_SECTION', lang: submitlanguage });
      }
    };

    Promise.all([
      FormEditService.getActions(formId, item.id).catch(() => ({ data: [] })),
      FormEditService.getFormFieldLogics(formId, item.id, sectionId).catch(() => ({ data: { logicDefinitions: [] } })),
    ])
      .then(([actionsRes, logicsRes]) => {
        const params = cloneFieldPayloadForDuplicate(item, submitlanguage);
        const createPromise = sectionId
          ? FormEditService.createField(formId, sectionId, params)
          : FormEditService.createUpdateFieldWithoutSection(formId, params, false);
        return createPromise.then((res) => ({ res, actionsRes, logicsRes }));
      })
      .then(({ res, actionsRes, logicsRes }) => {
        if (res.status !== 201 && res.status !== 200) {
          throw new Error('Failed to duplicate question');
        }
        const newFieldId = res.data?.id;
        if (!newFieldId) {
          throw new Error('Failed to duplicate question');
        }
        return FormEditService.reorderField(formId, newFieldId, { sectionId, prevFieldId: item.id })
          .then(() => fetchNewField(newFieldId))
          .then((newField) => ({ newFieldId, newField, actionsRes, logicsRes }));
      })
      .then(({ newFieldId, newField, actionsRes, logicsRes }) => {
        const newFieldData = newField || { id: newFieldId, options: [], gridConfig: null };
        const optionIdMap = buildOptionIdMap(item, newFieldData);
        const copyPromises = [];

        const actions = actionsRes.data || [];
        if (actions.length > 0) {
          copyPromises.push(
            FormEditService.createActions(formId, newFieldId, {
              actions: cloneActionsForDuplicate(actions, optionIdMap),
            }),
          );
        }

        const logics = logicsRes.data?.logicDefinitions || [];
        if (logics.length > 0) {
          copyPromises.push(
            FormEditService.saveFormFieldLogics(formId, newFieldId, sectionId, {
              logicDefinitions: cloneLogicsForDuplicate(logics, optionIdMap),
            }),
          );
        }

        return Promise.all(copyPromises).then(() => newFieldId);
      })
      .then((newFieldId) => {
        setSidebarItemsReorderedOn(Date.now());
        refreshForm();
        setLastCreatedItem({ type: 'field', id: newFieldId, sectionId });
        setSaveStatus(SAVE_STATUS.SAVED);
        toast.success('Question duplicated', { position: toast.POSITION.TOP_RIGHT });
      })
      .catch((err) => {
        setSaveStatus(SAVE_STATUS.ERROR);
        toast.error(err.message || 'Failed to duplicate question', { position: toast.POSITION.TOP_RIGHT });
      });
  }, [formId, formsectionsdata, submitlanguage, getFormDetails, findSectionIdForField]);

  const duplicateField = useCallback((item, knownSectionId = null) => {
    guardAction(() => applyDuplicateField(item, knownSectionId));
  }, [guardAction, applyDuplicateField]);

  const applyDuplicateSection = useCallback((section) => {
    if (!section?.id) return;
    setSaveStatus(SAVE_STATUS.SAVING);

    const duplicateFieldInto = (field, newSectionId, sourceSectionId, prevFieldId) => {
      if (!field?.id) return Promise.resolve(prevFieldId);
      return Promise.all([
        FormEditService.getActions(formId, field.id).catch(() => ({ data: [] })),
        FormEditService.getFormFieldLogics(formId, field.id, sourceSectionId).catch(() => ({ data: { logicDefinitions: [] } })),
      ]).then(([actionsRes, logicsRes]) => {
        const params = cloneFieldPayloadForDuplicate(field, submitlanguage);
        params.name = field.name;
        return FormEditService.createField(formId, newSectionId, params)
          .then((res) => {
            if (res.status !== 200 && res.status !== 201) throw new Error('Failed to duplicate section');
            const newFieldId = res.data?.id;
            if (!newFieldId) throw new Error('Failed to duplicate section');
            return FormEditService.reorderField(formId, newFieldId, { sectionId: newSectionId, prevFieldId })
              .then(() => FormEditService.getSectionDetails(formId, newSectionId, submitlanguage))
              .then((sectionRes) => sectionRes.data?.fields?.find((f) => f.id === newFieldId) || { id: newFieldId })
              .then((newFieldData) => {
                const optionIdMap = buildOptionIdMap(field, newFieldData);
                const copyPromises = [];
                const actions = actionsRes.data || [];
                if (actions.length > 0) {
                  copyPromises.push(FormEditService.createActions(formId, newFieldId, {
                    actions: cloneActionsForDuplicate(actions, optionIdMap),
                  }));
                }
                const logics = logicsRes.data?.logicDefinitions || [];
                if (logics.length > 0) {
                  copyPromises.push(FormEditService.saveFormFieldLogics(formId, newFieldId, newSectionId, {
                    logicDefinitions: cloneLogicsForDuplicate(logics, optionIdMap),
                  }));
                }
                return Promise.all(copyPromises).then(() => newFieldId);
              });
          });
      });
    };

    // `sourceSection` is already the fully-hydrated node from formdata.sections
    // (fields, subSections, and metadata included) — no per-section fetch needed.
    const duplicateOne = (sourceSection, parentSectionId, afterSectionId) => {
      const params = {
        title: `${sourceSection.title || 'Untitled section'} (Copy)`,
        description: sourceSection.description,
        layout: sourceSection.layout || 'INLINE',
        displayTitle: sourceSection.displayTitle ?? true,
        sortOrder: sourceSection.sortOrder ?? 100,
        pageHelpText: sourceSection.pageHelpText,
      };
      if (parentSectionId) params.parentSection = { id: parentSectionId };
      if (formdata?.formType === ASSESSMENTFORMTXT || formdata?.formType === GENERAL_FORM) {
        params.scoringMethod = sourceSection.scoringMethod || 'NONE';
        params.weightage = sourceSection.weightage || '';
      }
      if (submitlanguage) params.selectedLanguage = { id: submitlanguage };
      if (sourceSection.uom) params.uom = sourceSection.uom;
      if (sourceSection.multiSupportEnabled) params.multiSupportEnabled = sourceSection.multiSupportEnabled;

      return FormEditService.createSection(formId, params, null)
        .then((createRes) => {
          if (createRes.status !== 200 && createRes.status !== 201) {
            throw new Error('Failed to duplicate section');
          }
          const newSectionId = createRes.data?.id;
          if (!newSectionId) throw new Error('Failed to duplicate section');
          return FormEditService.reorderSection(formId, newSectionId, {
            sectionId: parentSectionId || 0,
            prevSectionId: afterSectionId || 0,
          }).then(() => newSectionId);
        })
        .then((newSectionId) => (
          (sourceSection.fields || []).reduce(
            (chain, field) => chain.then((prevFieldId) => duplicateFieldInto(field, newSectionId, sourceSection.id, prevFieldId)),
            Promise.resolve(0),
          ).then(() => newSectionId)
        ))
        .then((newSectionId) => (
          (sourceSection.subSections || []).reduce(
            (chain, sub) => chain.then((prevSubId) => duplicateOne(sub, newSectionId, prevSubId)),
            Promise.resolve(0),
          ).then(() => newSectionId)
        ));
    };

    const parentSectionId = findParentSectionId(section.id, formdata?.sections);

    duplicateOne(section, parentSectionId || 0, section.id)
      .then((newSectionId) => {
        setSidebarItemsReorderedOn(Date.now());
        getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage });
        setLastCreatedItem({ type: 'section', id: newSectionId, parentSectionId: parentSectionId || 0 });
        setSaveStatus(SAVE_STATUS.SAVED);
        toast.success('Section duplicated', { position: toast.POSITION.TOP_RIGHT });
      })
      .catch((err) => {
        setSaveStatus(SAVE_STATUS.ERROR);
        toast.error(err.message || 'Failed to duplicate section', { position: toast.POSITION.TOP_RIGHT });
      });
  }, [formId, formdata, submitlanguage, getFormDetails]);

  const duplicateSection = useCallback((section) => {
    guardAction(() => applyDuplicateSection(section));
  }, [guardAction, applyDuplicateSection]);

  const exportFormJson = useCallback(async () => {
    if (!formId) return;
    setLoaderState?.({ display: true, type: 'TEXTUAL' });
    try {
      const exportObject = await fetchFullFormExport(formId, submitlanguage);
      const validation = validateFormTree(exportObject);
      if (!validation.valid) {
        toast.error(`Export incomplete: ${validation.errors.join(' ')}`, { position: toast.POSITION.TOP_RIGHT });
        return;
      }
      downloadFormExport(exportObject, formdata?.name);
      toast.success('Form exported successfully', { position: toast.POSITION.TOP_RIGHT });
    } catch (err) {
      toast.error(err.message || 'Failed to export form', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setLoaderState?.({ display: false });
    }
  }, [formId, formdata, submitlanguage, setLoaderState]);

  const importFormJson = useCallback(async (file) => {
    if (!formId || !file) return;
    setLoaderState?.({ display: true, type: 'TEXTUAL' });
    try {
      const parsed = await readImportFile(file);
      const validation = validateImportFile(parsed);
      if (!validation.valid) {
        toast.error(validation.errors.join(' '), { position: toast.POSITION.TOP_RIGHT });
        return;
      }
      const result = await importFormIntoForm(formId, submitlanguage, formdata?.formType, parsed);
      getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage });
      const summary = `Import complete: ${result.sectionsCreated} section(s), ${result.fieldsCreated} question(s) added.`;
      if (result.warnings.length) {
        toast.error(`${summary} ${result.warnings.length} item(s) had issues: ${result.warnings.join(' ')}`, { position: toast.POSITION.TOP_RIGHT });
      } else {
        toast.success(summary, { position: toast.POSITION.TOP_RIGHT });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to import form', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setLoaderState?.({ display: false });
    }
  }, [formId, formdata, submitlanguage, getFormDetails, setLoaderState]);

  const copyFieldToClipboard = useCallback(async (item) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(item));
      toast.success('Copied to clipboard', { position: toast.POSITION.TOP_RIGHT });
    } catch {
      toast.error('Copy failed', { position: toast.POSITION.TOP_RIGHT });
    }
  }, []);

  const openMoveFieldModal = useCallback((field, fromSectionId = null) => {
    if (!field?.id) return;
    setFieldToMove(field);
    setFieldFromSectionId(fromSectionId ?? formsectionsdata?.id ?? 0);
    setMoveTargetSectionId('');
    setShowMoveFieldModal(true);
  }, [formsectionsdata]);

  const closeMoveFieldModal = useCallback(() => {
    setShowMoveFieldModal(false);
    setFieldToMove(null);
    setFieldFromSectionId(null);
    setMoveTargetSectionId('');
  }, []);

  const applyConfirmMoveFieldToSection = useCallback(() => {
    if (!fieldToMove || moveTargetSectionId === '') return;
    const targetSectionId = Number(moveTargetSectionId);
    const fromSectionId = fieldFromSectionId ?? 0;
    if (targetSectionId === fromSectionId) {
      toast.error('Question is already in this section', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    applyHandleFieldDrop(fieldToMove, { id: 0 }, targetSectionId);
    closeMoveFieldModal();
    toast.success('Question moved successfully', { position: toast.POSITION.TOP_RIGHT });
  }, [fieldToMove, moveTargetSectionId, fieldFromSectionId, applyHandleFieldDrop, closeMoveFieldModal]);

  const confirmMoveFieldToSection = useCallback(() => {
    guardAction(applyConfirmMoveFieldToSection);
  }, [guardAction, applyConfirmMoveFieldToSection]);

  const openMoveSectionModal = useCallback((section, fromParentId = null) => {
    if (!section?.id) return;
    const resolvedFromParentId = fromParentId ?? findParentSectionId(section.id) ?? 0;
    if (resolvedFromParentId === 0 && section.subSections?.length > 0) {
      toast.error('Cannot move a section that contains subsections. Move or remove its subsections first.', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setSectionToMove(section);
    setSectionFromParentId(resolvedFromParentId);
    setMoveTargetParentSectionId('');
    setShowMoveSectionModal(true);
  }, [formdata?.sections]);

  const closeMoveSectionModal = useCallback(() => {
    setShowMoveSectionModal(false);
    setSectionToMove(null);
    setSectionFromParentId(null);
    setMoveTargetParentSectionId('');
  }, []);

  const applyConfirmMoveSectionToParent = useCallback(() => {
    if (!sectionToMove || moveTargetParentSectionId === '') return;
    const targetParentId = Number(moveTargetParentSectionId);
    const fromParentId = sectionFromParentId ?? 0;
    const itemLabel = fromParentId === 0 ? 'Section' : 'Subsection';

    if (fromParentId === 0 && sectionToMove.subSections?.length > 0) {
      toast.error('Cannot move a section that contains subsections. Move or remove its subsections first.', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (targetParentId === fromParentId) {
      toast.error(`${itemLabel} is already in this section`, { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (targetParentId === sectionToMove.id) {
      toast.error('Cannot move a section into itself', { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    const collectDescendantIds = (section, ids = new Set()) => {
      (section.subSections || []).forEach((sub) => {
        ids.add(sub.id);
        collectDescendantIds(sub, ids);
      });
      return ids;
    };
    if (targetParentId !== 0 && collectDescendantIds(sectionToMove).has(targetParentId)) {
      toast.error('Cannot move a section into one of its own subsections', { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    const prevFormData = formdata;
    const data = { ...formdata, sections: JSON.parse(JSON.stringify(formdata.sections || [])) };

    // Searches both the top-level `sections` array and every nested `subSections`
    // array, so this handles detaching a top-level Section as well as a Subsection.
    const removeSubsection = (sections) => {
      const idx = (sections || []).findIndex((s) => s.id === sectionToMove.id);
      if (idx >= 0) {
        sections.splice(idx, 1);
        return true;
      }
      for (const section of sections || []) {
        if (section.subSections?.length && removeSubsection(section.subSections)) return true;
      }
      return false;
    };

    const insertSubsection = (sections) => {
      if (targetParentId === 0) {
        sections.unshift(sectionToMove);
        return true;
      }
      for (const section of sections || []) {
        if (section.id === targetParentId) {
          section.subSections = section.subSections || [];
          section.subSections.unshift(sectionToMove);
          return true;
        }
        if (section.subSections?.length && insertSubsection(section.subSections)) return true;
      }
      return false;
    };

    if (!removeSubsection(data.sections) || !insertSubsection(data.sections)) {
      toast.error(`Unable to move ${itemLabel.toLowerCase()}`, { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    setFormData(data);
    setGlbForm(data);
    setSidebarItemsReorderedOn(Date.now());

    if (formsectionsdata?.id === sectionToMove.id) {
      getSectionDetails(sectionToMove.id, null, submitlanguage);
    }

    reorderSectionApi(sectionToMove.id, targetParentId, 0, prevFormData);
    closeMoveSectionModal();
    toast.success(`${itemLabel} moved successfully`, { position: toast.POSITION.TOP_RIGHT });
  }, [
    sectionToMove, moveTargetParentSectionId, sectionFromParentId, formdata, setGlbForm,
    formsectionsdata, getSectionDetails, submitlanguage, reorderSectionApi, closeMoveSectionModal,
  ]);

  const confirmMoveSectionToParent = useCallback(() => {
    guardAction(applyConfirmMoveSectionToParent);
  }, [guardAction, applyConfirmMoveSectionToParent]);

  const handleColumnChange = useCallback((index, value) => {
    setGridData((prev) => {
      const newColumns = [...prev.columns];
      newColumns[index] = typeof newColumns[index] === 'object'
        ? { ...newColumns[index], name: value }
        : { name: value };
      return { ...prev, columns: newColumns };
    });
    markUnsaved();
  }, [markUnsaved]);

  const handleRowSetChange = useCallback((setIndex, field, value) => {
    setGridData((prev) => {
      const newRowSets = [...prev.rowSets];
      newRowSets[setIndex] = { ...newRowSets[setIndex], [field]: value };
      return { ...prev, rowSets: newRowSets };
    });
    markUnsaved();
  }, [markUnsaved]);

  const handleRowChange = useCallback((setIndex, rowIndex, field, value) => {
    setGridData((prev) => {
      const newRowSets = [...prev.rowSets];
      const rows = [...newRowSets[setIndex].rows];
      rows[rowIndex] = { ...rows[rowIndex], [field]: value };
      newRowSets[setIndex] = { ...newRowSets[setIndex], rows };
      return { ...prev, rowSets: newRowSets };
    });
    markUnsaved();
  }, [markUnsaved]);

  const addRowOrColumn = useCallback(() => {
    setGridData((prev) => ({
      columns: [...prev.columns, { name: '' }],
      rowSets: prev.rowSets.map((set) => ({
        ...set,
        rows: [...set.rows, { option: '', score: '' }],
      })),
    }));
    markUnsaved();
  }, [markUnsaved]);

  const removeRowOrColumn = useCallback((index) => {
    setGridData((prev) => {
      const newColumns = prev.columns.filter((_, i) => i !== index);
      const newRowSets = prev.rowSets.map((set) => ({
        ...set,
        rows: set.rows.filter((_, i) => i !== index),
      }));
      return newColumns.length === 0
        ? { columns: [], rowSets: [] }
        : { columns: newColumns, rowSets: newRowSets };
    });
    markUnsaved();
  }, [markUnsaved]);

  const addRowSet = useCallback(() => {
    setGridData((prev) => ({
      ...prev,
      rowSets: [...prev.rowSets, {
        name: '', code: '', help: '', sortOrder: '',
        rows: prev.columns.map(() => ({ option: '', score: '' })),
      }],
    }));
    markUnsaved();
  }, [markUnsaved]);

  const removeRowSet = useCallback((setIndex) => {
    setGridData((prev) => {
      const newRowSets = prev.rowSets.filter((_, i) => i !== setIndex);
      return newRowSets.length === 0
        ? { columns: [], rowSets: [] }
        : { ...prev, rowSets: newRowSets };
    });
    markUnsaved();
  }, [markUnsaved]);

  const addTableColumn = useCallback(() => {
    setTableData((prev) => ({
      columns: [...prev.columns, { name: '', columnType: 'TEXT', required: false, minimumValue: '', maximumValue: '' }],
    }));
    markUnsaved();
  }, [markUnsaved]);

  const removeTableColumn = useCallback((index) => {
    setTableData((prev) => ({ columns: prev.columns.filter((_, i) => i !== index) }));
    markUnsaved();
  }, [markUnsaved]);

  const handleTableColumnChange = useCallback((index, field, value) => {
    const isMinMaxField = field === 'minimumValue' || field === 'maximumValue';
    const sanitizedValue = isMinMaxField && value !== '' && Number(value) < 0 ? '0' : value;
    setTableData((prev) => {
      const columns = [...prev.columns];
      columns[index] = { ...columns[index], [field]: sanitizedValue };
      return { ...prev, columns };
    });
    markUnsaved();
  }, [markUnsaved]);

  // Drops any cellLinks entries and column/row-group span references whose
  // row/column no longer exists after a structural edit, instead of leaving
  // orphaned links/spans behind that can never be shown or edited again.
  const pruneGridQuestionRefs = (columns, rows, columnGroups, rowGroups, cellLinks) => {
    const colKeySet = new Set(columns.map((c) => c.key));
    const rowKeySet = new Set(rows.map((r) => r.key));
    const prunedColumnGroups = columnGroups
      .map((g) => ({ ...g, colKeys: g.colKeys.filter((k) => colKeySet.has(k)) }))
      .filter((g) => g.colKeys.length > 0);
    const prunedRowGroups = rowGroups
      .map((g) => ({ ...g, rowKeys: g.rowKeys.filter((k) => rowKeySet.has(k)) }))
      .filter((g) => g.rowKeys.length > 0);
    const prunedCellLinks = Object.fromEntries(
      Object.entries(cellLinks || {}).filter(([cellKey]) => {
        const [rowKey, colKey] = cellKey.split('_');
        return rowKeySet.has(rowKey) && colKeySet.has(colKey);
      }),
    );
    return { columnGroups: prunedColumnGroups, rowGroups: prunedRowGroups, cellLinks: prunedCellLinks };
  };

  const addGridQuestionColumn = useCallback(() => {
    setGridQuestionData((prev) => ({
      ...prev,
      columns: [...prev.columns, { key: genGridQuestionKey('col'), label: '' }],
    }));
    markUnsaved();
  }, [markUnsaved]);

  const removeGridQuestionColumn = useCallback((colIndex) => {
    setGridQuestionData((prev) => {
      const columns = prev.columns.filter((_, i) => i !== colIndex);
      const pruned = pruneGridQuestionRefs(columns, prev.rows, prev.columnGroups, prev.rowGroups, prev.cellLinks);
      return { ...prev, columns, ...pruned };
    });
    markUnsaved();
  }, [markUnsaved]);

  const updateGridQuestionColumnLabel = useCallback((colIndex, value) => {
    setGridQuestionData((prev) => {
      const columns = [...prev.columns];
      columns[colIndex] = { ...columns[colIndex], label: value };
      return { ...prev, columns };
    });
    markUnsaved();
  }, [markUnsaved]);

  // Spanning column header: covers a contiguous run of existing columns
  // (fromColKey..toColKey inclusive, in current column order). Purely an
  // overlay on top of the flat column list — it never has to be created
  // before columns exist, and removing it never touches the columns
  // themselves.
  const addGridQuestionColumnGroup = useCallback((fromColKey, toColKey, label) => {
    setGridQuestionData((prev) => {
      const fromIdx = prev.columns.findIndex((c) => c.key === fromColKey);
      const toIdx = prev.columns.findIndex((c) => c.key === toColKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [start, end] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      const colKeys = prev.columns.slice(start, end + 1).map((c) => c.key);
      return {
        ...prev,
        columnGroups: [...prev.columnGroups, { key: genGridQuestionKey('cg'), label, colKeys }],
      };
    });
    markUnsaved();
  }, [markUnsaved]);

  const removeGridQuestionColumnGroup = useCallback((groupIndex) => {
    setGridQuestionData((prev) => ({
      ...prev,
      columnGroups: prev.columnGroups.filter((_, i) => i !== groupIndex),
    }));
    markUnsaved();
  }, [markUnsaved]);

  const updateGridQuestionColumnGroupLabel = useCallback((groupIndex, value) => {
    setGridQuestionData((prev) => {
      const columnGroups = [...prev.columnGroups];
      columnGroups[groupIndex] = { ...columnGroups[groupIndex], label: value };
      return { ...prev, columnGroups };
    });
    markUnsaved();
  }, [markUnsaved]);

  const addGridQuestionRow = useCallback(() => {
    setGridQuestionData((prev) => ({
      ...prev,
      rows: [...prev.rows, { key: genGridQuestionKey('row'), label: '' }],
    }));
    markUnsaved();
  }, [markUnsaved]);

  const removeGridQuestionRow = useCallback((rowIndex) => {
    setGridQuestionData((prev) => {
      const rows = prev.rows.filter((_, i) => i !== rowIndex);
      const pruned = pruneGridQuestionRefs(prev.columns, rows, prev.columnGroups, prev.rowGroups, prev.cellLinks);
      return { ...prev, rows, ...pruned };
    });
    markUnsaved();
  }, [markUnsaved]);

  const updateGridQuestionRowLabel = useCallback((rowIndex, value) => {
    setGridQuestionData((prev) => {
      const rows = [...prev.rows];
      rows[rowIndex] = { ...rows[rowIndex], label: value };
      return { ...prev, rows };
    });
    markUnsaved();
  }, [markUnsaved]);

  // Full-width row heading: covers a contiguous run of existing rows, same
  // overlay approach as addGridQuestionColumnGroup above.
  const addGridQuestionRowGroup = useCallback((fromRowKey, toRowKey, label) => {
    setGridQuestionData((prev) => {
      const fromIdx = prev.rows.findIndex((r) => r.key === fromRowKey);
      const toIdx = prev.rows.findIndex((r) => r.key === toRowKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [start, end] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      const rowKeys = prev.rows.slice(start, end + 1).map((r) => r.key);
      return {
        ...prev,
        rowGroups: [...prev.rowGroups, { key: genGridQuestionKey('rg'), label, rowKeys }],
      };
    });
    markUnsaved();
  }, [markUnsaved]);

  const removeGridQuestionRowGroup = useCallback((groupIndex) => {
    setGridQuestionData((prev) => ({
      ...prev,
      rowGroups: prev.rowGroups.filter((_, i) => i !== groupIndex),
    }));
    markUnsaved();
  }, [markUnsaved]);

  const updateGridQuestionRowGroupLabel = useCallback((groupIndex, value) => {
    setGridQuestionData((prev) => {
      const rowGroups = [...prev.rowGroups];
      rowGroups[groupIndex] = { ...rowGroups[groupIndex], label: value };
      return { ...prev, rowGroups };
    });
    markUnsaved();
  }, [markUnsaved]);

  const updateGridQuestionCellLink = useCallback((cellKey, patch) => {
    setGridQuestionData((prev) => ({
      ...prev,
      cellLinks: { ...prev.cellLinks, [cellKey]: { ...prev.cellLinks[cellKey], ...patch } },
    }));
    markUnsaved();
  }, [markUnsaved]);

  const actionTaken = useCallback((checked, index, name) => {
    setActionsArray((prev) => {
      const array = [...prev];
      const types = Array.isArray(array[index].types) ? [...array[index].types] : [];
      if (checked) {
        if (!types.includes(name)) types.push(name);
      } else {
        const idx = types.indexOf(name);
        if (idx >= 0) types.splice(idx, 1);
      }
      array[index] = { ...array[index], types };
      return array;
    });
  }, []);

  const messageUpdate = useCallback((index, name, value) => {
    setActionsArray((prev) => {
      const array = [...prev];
      array[index] = { ...array[index], [name]: value };
      return array;
    });
  }, []);

  const emailUpdate = useCallback((index, name, value) => {
    setActionsArray((prev) => {
      const array = [...prev];
      array[index] = { ...array[index], [name]: value.split(',') };
      return array;
    });
  }, []);

  const chooseActionOption = useCallback((index, value) => {
    setActionsArray((prev) => {
      const array = [...prev];
      array[index] = { ...array[index], formOption: { id: value } };
      return array;
    });
  }, []);

  const addAction = useCallback(() => {
    setActionsArray((prev) => [...prev, { ...ACTIONS_ARR_OBJ }]);
  }, []);

  const onRemarksOptionSelect = useCallback((selectedList) => {
    setRemarksArr(selectedList.map((item) => ({ id: parseInt(item.id, 10) })));
    setChoiceOptions((prev) => prev.map((opt) => ({
      ...opt,
      showRemarks: selectedList.some((s) => +s.id === +opt.id),
    })));
    markUnsaved();
  }, [markUnsaved]);

  const onSelectDeSelectUoms = useCallback((selectedItem) => {
    setUOMData((prev) => {
      const uoms = [...(prev.relevantUoms || [])];
      const index = uoms.indexOf(selectedItem.value);
      if (index >= 0) uoms.splice(index, 1);
      else uoms.push(selectedItem.value);
      return { ...prev, relevantUoms: uoms };
    });
    markUnsaved();
  }, [markUnsaved]);

  const getSelectedUoms = useCallback(() => {
    return (uomData.relevantUoms || [])
      .map((uom) => inputUnits.find((u) => u.value === uom))
      .filter(Boolean);
  }, [uomData, inputUnits]);

  const handleExternalDependency = useCallback((enabled) => {
    setExtDependency({ enabled, value: enabled ? extDependency.value : '' });
    if (!enabled) setExtConfig(null);
    markUnsaved();
  }, [extDependency.value, markUnsaved]);

  const onExternalDependencyChange = useCallback((val) => {
    setExtDependency((prev) => ({ ...prev, value: val }));
    markUnsaved();
  }, [markUnsaved]);

  const onScopeChange = useCallback((val) => {
    setExtConfig({ emissionCategory: '', emissionScope: val });
    loadEmissionCats(val);
    markUnsaved();
  }, [loadEmissionCats, markUnsaved]);

  const changeQuestionType = useCallback((type) => {
    setSelectedAnsType(type);
    markUnsaved();
  }, [markUnsaved]);

  const handleOtherChange = useCallback((event) => {
    const val = event.currentTarget.checked;
    setOtherChecked(val);
    if (val) {
      setChoiceOptions((prev) => [...prev, { ...OPTION_OBJ, showOthers: true }]);
    } else {
      setChoiceOptions((prev) => prev.filter((x) => !x.showOthers));
    }
    markUnsaved();
  }, [markUnsaved]);

  const insertSectionAt = useCallback((parentSectionId, prevSectionId) => {
    openSectionModal(1, { kind: 'section', parentSectionId: parentSectionId || 0, prevSectionId });
  }, [openSectionModal]);

  const insertSubsectionAt = useCallback((parentSectionId, prevSectionId) => {
    openSectionModal(2, { kind: 'section', parentSectionId, prevSectionId });
  }, [openSectionModal]);

  const openSubsectionModal = useCallback((context = null) => openSectionModal(2, context), [openSectionModal]);

  // Flat list of every field on the form, any page/section — used by the
  // GRID question editor to let a cell be linked to any existing field.
  const allFormFields = useMemo(() => {
    if (!formdata) return [];
    const result = [...(formdata.fields || [])];
    const walk = (sections) => {
      (sections || []).forEach((section) => {
        result.push(...(section.fields || []));
        walk(section.subSections);
      });
    };
    walk(formdata.sections);
    return result;
  }, [formdata]);

  const publishForm = useCallback(() => (
    FormEditService.publishForm(formId)
      .then(() => {
        toast.success('Success', { position: toast.POSITION.TOP_RIGHT });
        setFormData((prev) => (prev ? { ...prev, status: 'PUBLISHED' } : prev));
        setGlbForm((prev) => (prev ? { ...prev, status: 'PUBLISHED' } : prev));
      })
      .catch(() => {})
  ), [formId, setGlbForm]);

  const initialLoadKeyRef = useRef(null);
  useEffect(() => {
    const key = `${formId}:${langId}`;
    if (initialLoadKeyRef.current === key) return; // guards against React.StrictMode's dev-only double-invoke
    initialLoadKeyRef.current = key;
    getFormDetails({ fetchFor: 'INITIAL', lang: langId });
    FormService.getLanguages().then((res) => setLanguages(res.data)).catch(() => {});
  }, [formId, langId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId >= 0) loadUnitsOfMeasure();
  }, [companyId, loadUnitsOfMeasure]);

  return {
    formId, langId, formdata, formsectionsdata, currentsection, submitlanguage, languages,
    isFormLoaded: formdata !== null,
    saveStatus, searchQuery, setSearchQuery, isSubsection, setIsSubsection, isWithinSection,
    sidebarItemsReorderedOn, showSectionModal, setShowSectionModal, sectionname, setSectionName,
    sectiondesp, setSectionDesp, scoringmethod, setScoringMethod, weightage, setWeightage,
    uom_popup, setUomPopup, uomRequiredPopup, setUomRequiredPopup, multiSupportEnabled,
    setMultiSupportEnabled, actionType, selParentSection, setSelParentSection, allSections,
    pageHelpText, setPageHelpText, validated, editEnable, field, setField, fieldeditid,
    selectedanswertype, setSelectedAnsType, showAnswerTypePicker, isNewField, choiceoptions,
    setChoiceOptions, optional, setOptional, scoreEnabled, setScoreEnabled, tabchoosed,
    setTabChoosed, helpText, setHelpText, otherChecked, setOtherChecked, otherText, setOtherText,
    otherMandatory, setOtherMandatory, remarksArr, setRemarksArr, remarkTitle, setRemarkTitle,
    remarkRequired, setRemarkRequired, allowRemark, allowComments, setAllowComments,
    uomRequired, setUomRequired, uomData, setUOMData, outputUnits, inputUnits, extDependency,
    setExtDependency, extConfig, setExtConfig, emissionCats, allowFileUpload, setAllowFileUpload,
    fileMandatory, setFileMandatory, hisDataSupported, setHisDataSupported,
    optionSelectionValidation, setOptionSelectionValidation, gridData, setGridData,
    tableData, setTableData, addTableColumn, removeTableColumn, handleTableColumnChange,
    gridQuestionData, setGridQuestionData, allFormFields,
    addGridQuestionColumnGroup, removeGridQuestionColumnGroup, updateGridQuestionColumnGroupLabel,
    addGridQuestionColumn, removeGridQuestionColumn, updateGridQuestionColumnLabel,
    addGridQuestionRowGroup, removeGridQuestionRowGroup, updateGridQuestionRowGroupLabel,
    addGridQuestionRow, removeGridQuestionRow, updateGridQuestionRowLabel,
    updateGridQuestionCellLink,
    actionsarray, setActionsArray, isConfirmModal, confirmMessage, isDeleteSection, updateform,
    setUpdateForm, getFormDetails, getSectionDetails, viewSection, getFieldWithoutSection, editField, selectQuestion,
    resetFieldProperties, handleSaveField, chooseAnswerType, startNewQuestion, closeQuestionTypePicker,
    insertQuestionAt,
    insertSectionAt, insertSubsectionAt, insertContext, deleteField, findSectionIdForField,
    lastCreatedItem, lastMovedItem, moveSectionToParent,
    confirmDeleteField, deleteSection, confirmDeleteSection, handleFieldDrop, handleSectionDrop,
    openSectionModal, editSectionModal, closeSectionModal, handleCreateSectionForm, getAllSections, getActionDetails,
    handleSaveActions, loadEmissionCats, duplicateField, duplicateSection, exportFormJson, importFormJson, copyFieldToClipboard,
    markUnsaved, isActionNeeded: () => isActionNeeded(selectedanswertype),
    isRemarkNeeded: () => isRemarkNeeded(selectedanswertype),
    handleRequired: (val) => {
      const newOptional = !val;
      const clearMandatoryFlags = !val;
      const previous = { optional, remarkRequired, fileMandatory };

      // Update the panel checkbox and the canvas's "*" indicator together, in
      // the same tick, so they can never show two different answers.
      setOptional(newOptional);
      patchFieldInTrees(fieldeditid, {
        optional: newOptional,
        ...(clearMandatoryFlags ? { remarkMandatory: false, fileMandatory: false } : {}),
      });
      if (clearMandatoryFlags) {
        setRemarkRequired(false);
        setFileMandatory(false);
      }
      markUnsaved();

      // Persist on its own right away — an already-created field has nothing
      // to wait for. A brand-new field has no id yet; its optional value just
      // rides along with the create call whenever that first Save happens.
      if (fieldeditid) {
        saveRequiredToggle(newOptional, clearMandatoryFlags, previous);
      }
    },
    handleScoreEnable: (val) => {
      setChoiceOptions(choiceoptions.map((item) => ({ ...item, score: null })));
      setScoreEnabled(val);
      markUnsaved();
    },
    addChoice: () => setChoiceOptions([...choiceoptions, { ...OPTION_OBJ }]),
    removeChoice: (index) => setChoiceOptions(choiceoptions.filter((_, i) => i !== index)),
    updateFieldChanged: (index) => (e) => {
      setChoiceOptions(choiceoptions.map((item, i) => (index === i ? { ...item, [e.target.name]: e.target.value } : item)));
      markUnsaved();
    },
    changeLanguage: (lang) => {
      setSubmitLanguage(lang);
      getFormDetails({ fetchFor: 'INITIAL', lang });
    },
    callbackFn: (val) => { setUpdateForm(val); getFormDetails({ fetchFor: 'INITIAL', lang: submitlanguage }); },
    handleColumnChange, handleRowSetChange, handleRowChange, addRowOrColumn, removeRowOrColumn,
    addRowSet, removeRowSet, actionTaken, messageUpdate, emailUpdate, chooseActionOption,
    addAction, onRemarksOptionSelect, onSelectDeSelectUoms, getSelectedUoms,
    handleExternalDependency, onExternalDependencyChange, onScopeChange, changeQuestionType,
    openSubsectionModal, handleOtherChange,
    showMoveFieldModal, fieldToMove, fieldFromSectionId, moveTargetSectionId,
    setMoveTargetSectionId, openMoveFieldModal, closeMoveFieldModal, confirmMoveFieldToSection,
    showMoveSectionModal, sectionToMove, sectionFromParentId, moveTargetParentSectionId,
    setMoveTargetParentSectionId, openMoveSectionModal, closeMoveSectionModal, confirmMoveSectionToParent,
    publishForm,
    hasUnsavedChanges, guardAction, showUnsavedChangesModal,
    confirmSaveAndContinue, confirmDiscardAndContinue, cancelUnsavedChangesModal,
  };
};

export default useFormEditor;
